import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPricingPlan } from "../utils/wellspoken.api";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const PricingSection = () => {
  const navigate = useNavigate();
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const data = await fetchPricingPlan();

        const normalized = data
          .filter((plan) => plan.planStatus === "Active")
          .map((plan) => {
            let features = [];

            try {
              if (plan.planFeature?.trim().startsWith("[")) {
                // If backend sends JSON string
                features = JSON.parse(plan.planFeature);
              } else {
                // Comma or newline separated string
                features = plan.planFeature
                  ?.split(/,|\n/)
                  .map((f) => f.trim())
                  .filter(Boolean);
              }
            } catch {
              features = [];
            }

            return {
              id: plan.planId,
              name: plan.planName,
              level: plan.level || "Intermediate",
              features: features.length
                ? features
                : ["Structured learning program"],
              duration: plan.pricetype || "1 Month",
              mode: plan.mode || "Offline",
              price: plan.price,
            };
          });


        setPricingPlans(normalized);
      } finally {
        setLoading(false);
      }
    };

    loadPricing();
  }, []);

  const handleEnroll = (plan) => {
    navigate("/contact", { state: { type: plan.name } });
  };

  return (
    <main className="relative bg-gradient-to-b from-white via-blue-50 to-indigo-50 py-10 lg:py-20 overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-200 to-purple-200 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-indigo-200 to-pink-200 rounded-full blur-3xl opacity-30" />
      </div>

      {/* Header */}
      <section className="relative max-w-7xl mx-auto px-6 text-center mb-20">
        <div className="inline-flex items-center gap-2 px-5 py-2 bg-white rounded-full shadow-lg border-2 border-blue-200 mb-8">
          <span className="text-2xl">💰</span>
          <span className="text-sm font-bold text-blue-600 uppercase tracking-wider">
            Pricing Plans
          </span>
        </div>

        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">
          Simple &{" "}
          <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Transparent Pricing
          </span>
        </h1>

        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Choose a plan that matches your learning goals and schedule.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="relative max-w-7xl mx-auto px-6">
        {loading ? (
          <div className="flex justify-center py-20 gap-3">
            <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce" />
            <div className="w-4 h-4 bg-indigo-500 rounded-full animate-bounce delay-100" />
            <div className="w-4 h-4 bg-purple-500 rounded-full animate-bounce delay-200" />
          </div>
        ) : (
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={32}
            slidesPerView={1}
            loop
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            pagination={{
              clickable: true,
              el: ".pricing"

            }}
            breakpoints={{
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="pricing-swiper"
          >
            {pricingPlans.map((plan) => (
              <SwiperSlide key={plan.id}>
                <article className="group">
                  <div className="relative bg-white rounded-[2rem] p-8 pt-0 shadow-2xl border-4 border-blue-100 hover:border-blue-500 hover:-translate-y-3 hover:shadow-blue-500/20 transition-all duration-500">

                    {/* Top Gradient */}
                    <div className="h-2 w-full rounded-t-2xl bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 mb-6" />

                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-bold rounded-full mb-6 shadow-lg">
                      🎓 {plan.level}
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-blue-600 transition">
                      {plan.name}
                    </h2>

                    {/* Feature */}
                    {/* <div className="flex gap-3 mb-6">
                      <span className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold text-sm">
                        ✓
                      </span>
                      <p className="text-sm text-gray-700">{plan.feature}</p>
                    </div> */}

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex gap-3">
                          <span className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold text-sm">
                            ✓
                          </span>
                          <p className="text-sm text-gray-700">{feature}</p>
                        </li>
                      ))}
                    </ul>


                    {/* Meta */}
                    <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-100">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {plan.duration.split(" ")[0]}
                        </p>
                        <p className="text-xs font-semibold text-gray-600 uppercase">
                          Months
                        </p>
                      </div>
                      <div className="text-center border-l-2 border-blue-200">
                        <p className="text-sm font-bold text-gray-900">
                          {plan.mode}
                        </p>
                        <p className="text-xs font-semibold text-gray-600 uppercase">
                          Mode
                        </p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-6 border-t-2 border-gray-100">
                      <div>
                        <p className="text-xs uppercase font-semibold text-gray-500">
                          Starting From
                        </p>
                        <p className="text-3xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                          ₹{plan.price}
                        </p>
                      </div>

                      <button
                        onClick={() => handleEnroll(plan)}
                        className="
    relative
    px-8 py-3
    text-base font-semibold text-white
    rounded-full
    bg-[linear-gradient(to_bottom,#b30000_0%,#ff3b3b_45%,#d10000_55%,#7a0000_100%)]
    
    /* base depth */
    shadow-[0_8px_18px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.35)]
    
    /* smooth animation */
    transition-all duration-300 ease-out
    
    /* hover effects */
    hover:brightness-110
    hover:shadow-[0_12px_28px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.45)]
    hover:-translate-y-[2px]
    
    /* active press */
    active:translate-y-[1px]
    active:shadow-[0_6px_12px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.25)]
  "
                      >
                        Enroll
                      </button>



                    </div>
                  </div>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        <div className="pricing mt-3 flex justify-center" />
      </section>
    </main>
  );
};

export default PricingSection;
